import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Divider } from "@/components/Divider";

const steps = ["Personal", "Property", "Financial", "Employment", "Review"];

export default function ApplyNow() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    loan_purpose: "purchase",
    loan_program: params.get("program") || "conventional",
    property_type: "single_family",
    property_address: "",
    property_city: "",
    property_state: "",
    property_zip: "",
    property_value: 450000,
    loan_amount: 360000,
    down_payment: 90000,
    annual_income: 0,
    monthly_debts: 0,
    credit_score_range: "700-739",
    employer_name: "",
    job_title: "",
    years_employed: 0,
    employment_status: "employed",
    consent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone) {
        toast.error("Please complete your name, email, and phone.");
        return false;
      }
    }
    if (step === 2) {
      if (form.annual_income <= 0) {
        toast.error("Please enter your annual income.");
        return false;
      }
    }
    if (step === 4 && !form.consent) {
      toast.error("Please accept the consent statement.");
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/applications", form);
      setDone(res.data);
      toast.success("Application received! We'll be in touch within 24 hours.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="apply-success">
        <CheckCircle2 className="text-[#00A651] mx-auto" size={64} />
        <h1 className="font-heading font-bold text-4xl text-black mt-6">Application received.</h1>
        <Divider className="mt-6 mx-auto" />
        <p className="font-sub italic text-lg text-gray-600 mt-6">
          Confirmation ID:{" "}
          <span className="font-heading font-bold text-black" data-testid="apply-confirmation-id">
            {done.id}
          </span>
        </p>
        <p className="text-gray-700 mt-4">
          A licensed originator will reach out within one business day at{" "}
          <span className="font-bold">{done.email}</span> or{" "}
          <span className="font-bold">{done.phone}</span>.
        </p>
        <a
          href="/"
          className="inline-block mt-8 bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
          data-testid="apply-back-home"
        >
          Back to Home
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="page-apply">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651] mb-4">Loan Application</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-black tracking-tight">
          Apply in about 10 minutes.
        </h1>
        <Divider className="my-6" />
        <p className="font-sub italic text-lg text-gray-600 max-w-2xl">
          Five quick steps. Your information is encrypted and reviewed by a licensed
          originator — never sold or shared.
        </p>

        {/* STEPPER */}
        <div className="mt-12 flex items-center" data-testid="apply-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`flex items-center gap-3 ${
                  i <= step ? "text-[#00A651]" : "text-gray-400"
                }`}
              >
                <div
                  className={`w-9 h-9 grid place-items-center rounded-sm font-bold ${
                    i < step
                      ? "bg-[#00A651] text-white"
                      : i === step
                      ? "bg-black text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {i < step ? <CheckCircle2 size={16} /> : i + 1}
                </div>
                <span className="hidden sm:inline text-xs uppercase tracking-wider font-bold">{s}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-[2px] mx-2 ${i < step ? "bg-[#00A651]" : "bg-gray-200"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* FORM */}
        <div className="mt-10 border border-gray-200 p-6 sm:p-10 rounded-sm bg-white">
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="step-personal">
              <Field label="First Name" testid="first_name" value={form.first_name} onChange={(v) => set("first_name", v)} />
              <Field label="Last Name" testid="last_name" value={form.last_name} onChange={(v) => set("last_name", v)} />
              <Field label="Email" testid="email" type="email" value={form.email} onChange={(v) => set("email", v)} />
              <Field label="Phone" testid="phone" value={form.phone} onChange={(v) => set("phone", v)} />
              <Field label="Date of Birth" testid="dob" type="date" value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} />
            </div>
          )}

          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="step-property">
              <SelectField
                label="Loan Purpose"
                testid="loan_purpose"
                value={form.loan_purpose}
                onChange={(v) => set("loan_purpose", v)}
                options={[
                  { v: "purchase", l: "Purchase" },
                  { v: "refinance", l: "Refinance" },
                  { v: "cash_out", l: "Cash-out Refinance" },
                ]}
              />
              <SelectField
                label="Loan Program"
                testid="loan_program"
                value={form.loan_program}
                onChange={(v) => set("loan_program", v)}
                options={[
                  { v: "conventional", l: "Conventional" },
                  { v: "fha", l: "FHA" },
                  { v: "va", l: "VA" },
                  { v: "usda", l: "USDA" },
                  { v: "jumbo", l: "Jumbo" },
                  { v: "refinance", l: "Refinance" },
                ]}
              />
              <SelectField
                label="Property Type"
                testid="property_type"
                value={form.property_type}
                onChange={(v) => set("property_type", v)}
                options={[
                  { v: "single_family", l: "Single Family" },
                  { v: "condo", l: "Condo" },
                  { v: "townhouse", l: "Townhouse" },
                  { v: "multi_family", l: "Multi-Family" },
                ]}
              />
              <Field label="Property Address" testid="property_address" value={form.property_address} onChange={(v) => set("property_address", v)} />
              <Field label="City" testid="property_city" value={form.property_city} onChange={(v) => set("property_city", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="State" testid="property_state" value={form.property_state} onChange={(v) => set("property_state", v)} />
                <Field label="ZIP" testid="property_zip" value={form.property_zip} onChange={(v) => set("property_zip", v)} />
              </div>
              <Field label="Property Value ($)" testid="property_value" type="number" value={form.property_value} onChange={(v) => set("property_value", Number(v))} />
              <Field label="Loan Amount ($)" testid="loan_amount" type="number" value={form.loan_amount} onChange={(v) => set("loan_amount", Number(v))} />
              <Field label="Down Payment ($)" testid="down_payment" type="number" value={form.down_payment} onChange={(v) => set("down_payment", Number(v))} />
            </div>
          )}

          {step === 2 && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="step-financial">
              <Field label="Annual Income ($)" testid="annual_income" type="number" value={form.annual_income} onChange={(v) => set("annual_income", Number(v))} />
              <Field label="Monthly Debts ($)" testid="monthly_debts" type="number" value={form.monthly_debts} onChange={(v) => set("monthly_debts", Number(v))} />
              <SelectField
                label="Credit Score Range"
                testid="credit_score_range"
                value={form.credit_score_range}
                onChange={(v) => set("credit_score_range", v)}
                options={[
                  { v: "below-580", l: "Below 580" },
                  { v: "580-619", l: "580 - 619" },
                  { v: "620-659", l: "620 - 659" },
                  { v: "660-699", l: "660 - 699" },
                  { v: "700-739", l: "700 - 739" },
                  { v: "740-799", l: "740 - 799" },
                  { v: "800-plus", l: "800+" },
                ]}
              />
            </div>
          )}

          {step === 3 && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="step-employment">
              <SelectField
                label="Employment Status"
                testid="employment_status"
                value={form.employment_status}
                onChange={(v) => set("employment_status", v)}
                options={[
                  { v: "employed", l: "Employed" },
                  { v: "self_employed", l: "Self-Employed" },
                  { v: "retired", l: "Retired" },
                  { v: "other", l: "Other" },
                ]}
              />
              <Field label="Employer Name" testid="employer_name" value={form.employer_name} onChange={(v) => set("employer_name", v)} />
              <Field label="Job Title" testid="job_title" value={form.job_title} onChange={(v) => set("job_title", v)} />
              <Field label="Years Employed" testid="years_employed" type="number" value={form.years_employed} onChange={(v) => set("years_employed", Number(v))} />
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-review">
              <h3 className="font-heading font-bold text-xl text-black">Review your application</h3>
              <Divider className="my-4" />
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Review k="Name" v={`${form.first_name} ${form.last_name}`} />
                <Review k="Email" v={form.email} />
                <Review k="Phone" v={form.phone} />
                <Review k="Program" v={form.loan_program.toUpperCase()} />
                <Review k="Purpose" v={form.loan_purpose} />
                <Review k="Property" v={`${form.property_address || "—"}, ${form.property_city} ${form.property_state} ${form.property_zip}`} />
                <Review k="Property Value" v={`$${(form.property_value || 0).toLocaleString()}`} />
                <Review k="Loan Amount" v={`$${(form.loan_amount || 0).toLocaleString()}`} />
                <Review k="Down Payment" v={`$${(form.down_payment || 0).toLocaleString()}`} />
                <Review k="Annual Income" v={`$${(form.annual_income || 0).toLocaleString()}`} />
                <Review k="Monthly Debts" v={`$${(form.monthly_debts || 0).toLocaleString()}`} />
                <Review k="Credit Range" v={form.credit_score_range} />
                <Review k="Employer" v={`${form.employer_name || "—"} · ${form.job_title || "—"}`} />
              </div>

              <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="consent-label">
                <Checkbox
                  checked={form.consent}
                  onCheckedChange={(c) => set("consent", !!c)}
                  data-testid="consent-checkbox"
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  I authorize DW Investment Group to contact me regarding my application
                  and to obtain a credit report as needed to evaluate my eligibility. I
                  understand this is not a commitment to lend.
                </span>
              </label>
            </div>
          )}

          {/* CONTROLS */}
          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={back}
              disabled={step === 0}
              data-testid="apply-back-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-sm font-bold uppercase tracking-wider text-black disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < steps.length - 1 ? (
              <button
                onClick={next}
                data-testid="apply-next-btn"
                className="inline-flex items-center gap-2 bg-black hover:bg-[#00A651] text-white px-7 py-3 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors"
              >
                Next <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="apply-submit-btn"
                className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-50 text-white px-7 py-3 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors"
              >
                {submitting ? "Submitting…" : "Submit Application"}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-black">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`apply-${testid}`}
        className="mt-2 rounded-sm"
      />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-black">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`apply-${testid}`} className="mt-2 rounded-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v} data-testid={`apply-${testid}-${o.v}`}>
              {o.l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Review({ k, v }) {
  return (
    <div className="border-l-2 border-[#ED1C24] pl-3">
      <div className="text-xs uppercase tracking-wider text-gray-500">{k}</div>
      <div className="font-heading font-bold text-black break-words">{v || "—"}</div>
    </div>
  );
}
